import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { checkout as checkoutApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await checkoutApi.status(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState("error");
        } else {
          setTimeout(poll, 2000);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <main data-testid="checkout-success-page" className="min-h-[70vh] bg-zinc-50 flex items-center justify-center p-6">
      <div className="bg-white border border-zinc-200 max-w-md w-full p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#4F46E5] animate-spin" />
            <h1 className="font-display font-black text-2xl mb-2">Procesando tu pago...</h1>
            <p className="text-zinc-600 text-sm">Esto puede tardar unos segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-emerald-600" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-2">
              Pago confirmado
            </p>
            <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
              ¡Bienvenido a Kursos Online!
            </h1>
            <p className="text-zinc-600 mb-6">
              Tu acceso ha sido activado. Ya puedes empezar a aprender.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="goto-dashboard-btn"
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white py-3.5 font-semibold"
            >
              Ir a mis cursos
            </button>
          </>
        )}
        {(state === "expired" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 mx-auto mb-4 text-red-500" />
            <h1 className="font-display font-black text-2xl mb-2">
              {state === "expired" ? "Sesión expirada" : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-zinc-600 mb-6 text-sm">
              Si crees que es un error, escribe a Coni o contáctanos.
            </p>
            <Link to="/" className="block w-full bg-zinc-900 text-white py-3.5 font-semibold">
              Volver al inicio
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default CheckoutSuccess;
